import React, { useState, useEffect } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { createCalendarDate } from '../../utils/dateUtils';

const locales = {
  'en-US': require('date-fns/locale/en-US')
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales
});

function ProjectCalendar({ project, phaseStatusTypes, stepStatusTypes, handlePhaseClick, handleStepClick }) {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (project) {
      const calendarEvents = [];
      
      // Add project due date if exists
      if (project.due_date) {
        const projectDate = createCalendarDate(project.due_date);
        calendarEvents.push({
          id: `project-${project.id}`,
          title: `Project: ${project.name}`,
          start: projectDate,
          end: projectDate,
          allDay: true,
          resource: {
            type: 'project',
            id: project.id,
            color: '#3b82f6', // blue-500
            textColor: 'white'
          }
        });
      }
      
      // Add phases due dates
      if (project.phases && project.phases.length > 0) {
        project.phases.forEach(phase => {
          if (phase.due_date) {
            const phaseStatus = phaseStatusTypes.find(st => st.name === phase.status);
            const phaseDate = createCalendarDate(phase.due_date);
            calendarEvents.push({
              id: `phase-${phase.id}`,
              title: `Phase: ${phase.name}`,
              start: phaseDate,
              end: phaseDate,
              allDay: true,
              resource: {
                type: 'phase',
                id: phase.id,
                color: phaseStatus ? phaseStatus.color : '#4b5563', // gray-600
                textColor: 'white'
              }
            });
          }
          
          // Add steps due dates
          if (phase.steps && phase.steps.length > 0) {
            phase.steps.forEach(step => {
              if (step.due_date) {
                const stepStatus = stepStatusTypes.find(st => st.name === step.status);
                const stepDate = createCalendarDate(step.due_date);
                calendarEvents.push({
                  id: `step-${step.id}`,
                  title: `Step: ${step.name}`,
                  start: stepDate,
                  end: stepDate,
                  allDay: true,
                  resource: {
                    type: 'step',
                    id: step.id,
                    color: stepStatus ? stepStatus.color : '#6b7280', // gray-500
                    textColor: 'white'
                  }
                });
              }
            });
          }
        });
      }
      
      setEvents(calendarEvents);
    }
  }, [project, phaseStatusTypes, stepStatusTypes]);

  const eventStyleGetter = (event) => {
    const resource = event.resource || {};
    return {
      style: {
        backgroundColor: resource.color,
        color: resource.textColor,
        borderRadius: '4px',
        border: 'none',
        display: 'block',
        padding: '2px 5px'
      }
    };
  };

  const handleEventClick = (event) => {
    const resource = event.resource;
    if (resource) {
      if (resource.type === 'phase' && handlePhaseClick) {
        handlePhaseClick(resource.id);
      } else if (resource.type === 'step' && handleStepClick) {
        // Find the step object
        let targetStep = null;
        project.phases.forEach(phase => {
          const foundStep = phase.steps.find(step => step.id === resource.id);
          if (foundStep) targetStep = foundStep;
        });
        
        if (targetStep) {
          handleStepClick(targetStep);
        }
      }
    }
  };

  return (
    <div className="h-[600px] bg-white p-4">
      <Calendar
        localizer={localizer}
        events={events}
        startAccessor="start"
        endAccessor="end"
        style={{ height: '100%' }}
        views={['month', 'week', 'day']}
        eventPropGetter={eventStyleGetter}
        onSelectEvent={handleEventClick}
        popup
      />
    </div>
  );
}

export default ProjectCalendar; 